import { LessonController, LessonPhases } from './lesson';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup, flashCard } from './quiz/feedback';
import { getUiSoundPlayer } from './ui-sounds';
import { generateMul2x1 } from './generators/math/mul2x1';
import { generateMul2x2 } from './generators/math/mul2x2';
import { generateDivRemainder } from './generators/math/div-remainder';
import { generateTwoStep } from './generators/math/two-step';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP, skillUnlocked } from '../shared/progress';
import { findOrCreateKP, recordAttempt } from '../shared/student-kp';
import { FACTIONS, factionForSubject } from '../shared/factions';
import { SKILL_CATALOG } from '../shared/skills';
import { mountEscapeButton } from './escape-button';
import { recordMistake, deriveQuestionId } from '../shared/mistake-log';
import { mountMascot } from './mascot/mascot-ui';
import type { GeneratedQuestion, Difficulty } from './generators/math/types';

/**
 * Result passed to onDone when a lesson ends.
 * - `newUnlocks`: skill IDs unlocked by this lesson (populated only on completion).
 * - `exited`: true when the kid hit ✕ 離開 mid-lesson; main.ts routes these
 *   back to chapter-page instead of home (task #13, round-1+2 kid complaint).
 *   Absent / false means natural completion → celebration → home flow.
 */
export type LearnDoneCallback = (result: { newUnlocks: string[]; exited?: boolean }) => void;

type GenFn = (seed: number, difficulty: Difficulty) => GeneratedQuestion;

/**
 * Build a math chapter that mixes KPs based on the player's current XP.
 *   XP  < 100 : only mul2x1 (warmup / easy ground)
 *   XP 100-299: mul2x1 + mul2x2
 *   XP >= 300 : all 4 KPs in rotation
 */
function buildMathChapter(seedBase: number, xp: number): LessonPhases {
  const easy: GenFn[] = [generateMul2x1];
  const mid: GenFn[] = xp >= 100 ? [generateMul2x1, generateMul2x2] : [generateMul2x1];
  const hard: GenFn[] = xp >= 300
    ? [generateMul2x2, generateDivRemainder, generateTwoStep]
    : mid;

  // Pick a generator from a pool deterministically based on offset
  function pick(pool: GenFn[], offset: number): GenFn {
    return pool[(seedBase + offset) % pool.length];
  }

  function make(offset: number, d: Difficulty, pool: GenFn[]): GeneratedQuestion {
    return pick(pool, offset)(seedBase + offset, d);
  }

  return {
    warmup:    [make(0, 1, easy), make(1, 1, easy)],
    guide:     [make(2, 1, easy), make(3, 1, mid), make(4, 2, mid)],
    challenge: [make(5, 2, mid), make(6, 2, hard), make(7, 3, hard), make(8, 3, hard), make(9, 3, hard)],
  };
}

export function runMathMul2x1Lesson(container: HTMLElement, onDone: LearnDoneCallback): void {
  runMathLesson(container, onDone);
}

export function runMathLesson(container: HTMLElement, onDone: LearnDoneCallback): void {
  const seed = Date.now() & 0xffff;

  // Load (or create) progress early so we can read XP to choose difficulty mix
  let progress = loadProgress();
  if (!progress) progress = createFreshProgress('指揮官');

  const mathXP = progress.factionXP[factionForSubject('math')] ?? 0;
  const phases = buildMathChapter(seed, mathXP);
  const kp = findOrCreateKP(progress, 'math.mul.2x1', 'math');

  const lesson = new LessonController(phases, 1, (correct) => {
    recordAttempt(kp, correct ? 'correct' : 'wrong', new Date().toISOString());
    saveProgress(progress!);
  });

  // C07 Mascot — 學習端陪伴角色
  const mascot = mountMascot();

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    onAnswer: (answer: number | string) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      // Capture question before submitAnswer advances the index
      const currentQ = lesson.currentQuestion();
      const currentPhase = lesson.state;
      const result = lesson.submitAnswer(answer);
      if (card) flashCard(card, result.correct ? 'correct' : 'wrong');
      // C07 Mascot react — 300ms delay 讓 kid 先看到題目對錯閃光
      setTimeout(() => mascot.controller.react(result.combo, result.correct), 300);
      if (result.correct) {
        getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        getUiSoundPlayer().play('answer-wrong', 0.4);
        // Record mistake for non-warmup wrong answers only
        if (currentPhase !== 'warmup') {
          // v1.1 worked-example: schema-build 不會走到 mistake 路徑;
          // 仍 narrow 一下避免 union 觸發 TS2339。
          const correctAns =
            currentQ.kind === 'mcq'
              ? currentQ.options[currentQ.correctAnswer]
              : currentQ.kind === 'fill'
                ? currentQ.correctAnswer
                : '';
          recordMistake(progress!, {
            questionId: deriveQuestionId(currentQ.prompt),
            kp: currentQ.kp,
            subject: 'math',
            prompt: currentQ.prompt,
            correctAnswer: correctAns,
            userAnswer: answer,
          });
          saveProgress(progress!);
        }
      }
      if (result.xp > 0) {
        const faction = FACTIONS[factionForSubject('math')];
        getUiSoundPlayer().play('xp-popup', 0.35);
        showXpPopup(`+${Math.round(result.xp)} ${faction.emoji}`, '#ffd54f');
      } else if (!result.correct) {
        showXpPopup('沒關係 +2', '#80cbc4');
      }

      // Brief delay then render the next question (or finish)
      setTimeout(() => {
        if (lesson.state === 'done') {
          finish();
        } else {
          renderCurrent();
        }
      }, result.correct ? 500 : 900);
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    mascot.destroy();
    unmountEscape();
    onDone({ newUnlocks: [], exited: true });
  });

  function renderCurrent(): void {
    if (!overlay) return;
    overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
  }

  function finish(): void {
    unmountEscape();
    overlay?.destroy();
    overlay = null;
    mascot.destroy();

    // Persist XP into the math faction (reuse the progress object already held in closure)
    const faction = factionForSubject('math');
    const xp = Math.round(lesson.totalXP);
    addFactionXP(progress!, faction, xp);

    // Unlock meteor cannon if threshold crossed
    const newUnlocks: string[] = [];
    for (const skill of SKILL_CATALOG) {
      if (skill.faction === 'builtin') continue;
      if (skillUnlocked(progress!, skill.id)) continue;
      if (progress!.factionXP[skill.faction] >= skill.cost) {
        progress!.unlockedSkills.push(skill.id);
        newUnlocks.push(skill.id);
      }
    }
    saveProgress(progress!);
    onDone({ newUnlocks });
  }

  renderCurrent();
}
